import { useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Filter,
  MapPin,
  MessageSquare,
  Phone,
  Send,
  Sparkles,
  Tag,
  Users,
} from "lucide-react";
import { Spinner } from "../components/Spinner.tsx";
import { db } from "../db.ts";
import {
  countAdminPushRecipients,
  type PushCountResult,
  sendAdminPush,
} from "../adminApi.ts";
import { popularCategories, popularLocations } from "../popularFilters.ts";
import { Input } from "../components/ui/index.ts";

export const AdminPushes = () => {
  const auth = db.useAuth().user;

  // Filter State
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");
  const [limit, setLimit] = useState<number>(50);

  // Message State
  const [message, setMessage] = useState("");

  // Test Run State
  const [testPhone, setTestPhone] = useState("");
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<
    {
      ok: boolean;
      text: string;
    } | null
  >(null);

  // Count & Broadcast State
  const [countLoading, setCountLoading] = useState(false);
  const [countResult, setCountResult] = useState<PushCountResult | null>(null);
  const [countError, setCountError] = useState("");

  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult, setSendResult] = useState<
    {
      ok: boolean;
      text: string;
    } | null
  >(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Invalidate count state whenever filters change
  const handleLocationChange = (val: string) => {
    setLocation(val);
    setCountResult(null);
    setSendResult(null);
  };

  const handleCategoryChange = (val: string) => {
    setCategory(val);
    setCountResult(null);
    setSendResult(null);
  };

  const handleLimitChange = (val: number) => {
    const clamped = Math.min(Math.max(val || 1, 1), 100);
    setLimit(clamped);
    setCountResult(null);
    setSendResult(null);
  };

  // 1. Test Run Push
  const handleSendTestPush = async () => {
    const token = auth?.refresh_token;
    if (!token) {
      setTestResult({ ok: false, text: "Authentication token missing." });
      return;
    }
    if (!message.trim()) {
      setTestResult({ ok: false, text: "Please write a message first." });
      return;
    }
    if (!testPhone.trim()) {
      setTestResult({
        ok: false,
        text: "Please enter a valid test phone number.",
      });
      return;
    }

    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await sendAdminPush(token, {
        message: message.trim(),
        testPhone: testPhone.trim(),
      });
      if (res.ok) {
        setTestResult({
          ok: true,
          text: `Test push sent successfully to ${res.testPhone || testPhone}!`,
        });
      } else {
        setTestResult({
          ok: false,
          text: res.error || "Failed to send test push.",
        });
      }
    } catch (err) {
      setTestResult({
        ok: false,
        text: err instanceof Error ? err.message : "Error sending test push.",
      });
    } finally {
      setTestLoading(false);
    }
  };

  // 2. Step 1: Count Recipients
  const handleCountRecipients = async () => {
    const token = auth?.refresh_token;
    if (!token) {
      setCountError("Authentication token missing.");
      return;
    }

    setCountLoading(true);
    setCountError("");
    setCountResult(null);
    setSendResult(null);

    try {
      const res = await countAdminPushRecipients(token, {
        location: location.trim() || undefined,
        category: category.trim() || undefined,
        limit,
      });
      setCountResult(res);
    } catch (err) {
      setCountError(
        err instanceof Error ? err.message : "Failed to count recipients.",
      );
    } finally {
      setCountLoading(false);
    }
  };

  // 3. Step 2: Send Push to Audience
  const handleSendAudiencePush = async () => {
    setShowConfirmModal(false);
    const token = auth?.refresh_token;
    if (!token) {
      setSendResult({ ok: false, text: "Authentication token missing." });
      return;
    }
    if (!message.trim()) {
      setSendResult({ ok: false, text: "Please write a message first." });
      return;
    }

    setSendLoading(true);
    setSendResult(null);

    try {
      const res = await sendAdminPush(token, {
        location: location.trim() || undefined,
        category: category.trim() || undefined,
        limit,
        message: message.trim(),
      });
      if (res.ok) {
        setSendResult({
          ok: true,
          text: `Successfully dispatched push to ${res.sentCount} recipients!`,
        });
        setCountResult(null); // Reset count after broadcast
      } else {
        setSendResult({
          ok: false,
          text: res.error || "Failed to send broadcast.",
        });
      }
    } catch (err) {
      setSendResult({
        ok: false,
        text: err instanceof Error ? err.message : "Error broadcasting push.",
      });
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Send className="text-purple-400" size={24} />
          Push Notifications & Broadcasts
        </h2>
        <p className="text-gray-400 text-sm mt-1">
          Target and notify users by location and category. First count the
          audience (up to 100 users max), then test on a test number or send.
        </p>
      </div>

      {/* Target Audience Filters */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-6">
        <div className="flex items-center gap-2 text-white font-semibold text-base border-b border-white/10 pb-3">
          <Filter size={18} className="text-purple-400" />
          Target Audience Filters
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Location */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-300 flex items-center gap-1.5">
              <MapPin size={14} className="text-purple-400" />
              Location / City / Area
            </label>
            <Input
              type="text"
              placeholder="e.g. Tel Aviv, Center, Israel..."
              value={location}
              onChange={(e) => handleLocationChange(e.target.value)}
            />
            <div className="flex flex-wrap gap-1.5 pt-1">
              {popularLocations.slice(0, 5).map((loc) => (
                <button
                  key={loc}
                  type="button"
                  onClick={() =>
                    handleLocationChange(location === loc ? "" : loc)}
                  className={`text-[11px] px-2 py-0.5 rounded-md transition ${
                    location === loc
                      ? "bg-purple-600 text-white font-medium"
                      : "bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white"
                  }`}
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-300 flex items-center gap-1.5">
              <Tag size={14} className="text-purple-400" />
              Category / Interest
            </label>
            <Input
              type="text"
              placeholder="e.g. techno, party, sports..."
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
            />
            <div className="flex flex-wrap gap-1.5 pt-1">
              {popularCategories.slice(0, 5).map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() =>
                    handleCategoryChange(category === cat ? "" : cat)}
                  className={`text-[11px] px-2 py-0.5 rounded-md transition ${
                    category === cat
                      ? "bg-purple-600 text-white font-medium"
                      : "bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* User Limit (Max 100) */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-300 flex items-center gap-1.5">
              <Users size={14} className="text-purple-400" />
              Recipient Limit (Max 100)
            </label>
            <Input
              type="number"
              min={1}
              max={100}
              value={limit}
              onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
            />
            <p className="text-[11px] text-gray-400">
              Safety guard: Maximum 100 users per send.
            </p>
          </div>
        </div>
      </div>

      {/* Message Composer */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center gap-2 text-white font-semibold text-base">
            <MessageSquare size={18} className="text-purple-400" />
            Push Message / Prompt
          </div>
          <span className="text-xs text-gray-400">
            {message.length} characters
          </span>
        </div>

        <textarea
          rows={4}
          placeholder="Enter the push update or announcement for users. For instance: 'We just launched ticket buying and selling directly on Agent FOMO! Let me know if you want to check it out.'"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded-lg p-3.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition resize-y font-mono"
        />

        <div className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-3 text-xs text-purple-200/80 flex items-start gap-2">
          <Sparkles size={16} className="text-purple-400 shrink-0 mt-0.5" />
          <span>
            The agent will deliver this proactive task to recipients over
            WhatsApp in their preferred language (Hebrew / English) using
            approved templates if outside the 24-hour window.
          </span>
        </div>
      </div>

      {/* Test Run Box */}
      <div className="bg-purple-950/30 border border-purple-500/30 rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2 text-purple-200 font-semibold text-base">
          <Phone size={18} className="text-purple-400" />
          Test Run On Specific Number
        </div>
        <p className="text-gray-300 text-xs">
          Send a test push to a specific phone number first to review how the
          message is formatted and delivered before broadcasting to the whole
          audience.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="text"
            placeholder="+9725..."
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            className="flex-1"
          />
          <button
            type="button"
            onClick={handleSendTestPush}
            disabled={testLoading || !message.trim() || !testPhone.trim()}
            className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition shadow-lg shadow-purple-600/20"
          >
            {testLoading ? <Spinner className="h-4 w-4" /> : <Send size={15} />}
            Send Test Push
          </button>
        </div>

        {testResult && (
          <div
            className={`p-3 rounded-lg text-xs flex items-center gap-2 ${
              testResult.ok
                ? "bg-green-500/20 border border-green-500/30 text-green-300"
                : "bg-red-500/20 border border-red-500/30 text-red-300"
            }`}
          >
            {testResult.ok
              ? <CheckCircle2 size={16} className="shrink-0 text-green-400" />
              : <AlertCircle size={16} className="shrink-0 text-red-400" />}
            <span>{testResult.text}</span>
          </div>
        )}
      </div>

      {/* Audience Broadcast Workflow: Step 1 Count -> Step 2 Send */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-6">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div className="flex items-center gap-2 text-white font-semibold text-base">
            <Users size={18} className="text-purple-400" />
            Audience Broadcast (2-Step Execution)
          </div>
          {countResult && (
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-500/20 text-green-300 border border-green-500/30 flex items-center gap-1.5">
              <CheckCircle2 size={13} />
              Count Verified
            </span>
          )}
        </div>

        {/* Step 1: Count */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
              Step 1: Count Matching Audience
            </span>
            <button
              type="button"
              onClick={handleCountRecipients}
              disabled={countLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition"
            >
              {countLoading
                ? <Spinner className="h-4 w-4" />
                : <Users size={14} className="text-purple-400" />}
              {countResult ? "Re-count Audience" : "Count Matching Users"}
            </button>
          </div>

          {countError && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-xs text-red-300 flex items-center gap-2">
              <AlertCircle size={16} className="shrink-0 text-red-400" />
              <span>{countError}</span>
            </div>
          )}

          {countResult && (
            <div className="bg-black/30 border border-white/10 rounded-lg p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-200">
                <div>
                  <span className="text-gray-400 text-xs block">
                    Total Matching Users
                  </span>
                  <span className="text-lg font-bold text-white">
                    {countResult.totalMatching}
                  </span>
                </div>
                <div className="h-8 w-px bg-white/10" />
                <div>
                  <span className="text-gray-400 text-xs block">
                    Target Send Count (Capped by limit)
                  </span>
                  <span className="text-lg font-bold text-purple-400">
                    {countResult.targetCount}
                  </span>
                </div>
                <div className="h-8 w-px bg-white/10" />
                <div>
                  <span className="text-gray-400 text-xs block">Filters</span>
                  <span className="text-xs text-gray-300">
                    {location ? `Location: "${location}"` : "All locations"}
                    {" • "}
                    {category ? `Category: "${category}"` : "All categories"}
                  </span>
                </div>
              </div>

              {countResult.sample && countResult.sample.length > 0 && (
                <div className="pt-2 border-t border-white/5">
                  <span className="text-[11px] font-medium text-gray-400 block mb-1.5">
                    Sample Matching Recipients (First{" "}
                    {countResult.sample.length}):
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {countResult.sample.map((u, i) => (
                      <span
                        key={i}
                        className="text-[11px] bg-white/5 border border-white/10 px-2.5 py-1 rounded text-gray-300 font-mono"
                      >
                        {u.userName ? `${u.userName} (` : ""}
                        {u.userId.slice(0, 4)}...{u.userId.slice(-4)}
                        {u.userName ? ")" : ""}
                        {u.location ? ` - ${u.location}` : ""}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Step 2: Actually Send */}
        <div className="pt-4 border-t border-white/10 space-y-3">
          <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider block">
            Step 2: Dispatch Broadcast
          </span>

          {!countResult
            ? (
              <p className="text-xs text-gray-400 italic">
                Please count the matching audience in Step 1 first to unlock
                sending.
              </p>
            )
            : countResult.targetCount === 0
            ? (
              <p className="text-xs text-amber-400">
                No matching users found for the selected filters.
              </p>
            )
            : (
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(true)}
                  disabled={sendLoading || !message.trim()}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition shadow-lg shadow-purple-600/25"
                >
                  {sendLoading
                    ? <Spinner className="h-4 w-4" />
                    : <Send size={16} />}
                  Send Push to {countResult.targetCount} Users
                </button>
                <span className="text-xs text-gray-400">
                  Pushes will be sent sequentially with a safety interval.
                </span>
              </div>
            )}

          {sendResult && (
            <div
              className={`p-4 rounded-lg text-sm flex items-center gap-3 ${
                sendResult.ok
                  ? "bg-green-500/20 border border-green-500/30 text-green-300"
                  : "bg-red-500/20 border border-red-500/30 text-red-300"
              }`}
            >
              {sendResult.ok
                ? <CheckCircle2 size={18} className="shrink-0 text-green-400" />
                : <AlertCircle size={18} className="shrink-0 text-red-400" />}
              <span>{sendResult.text}</span>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmModal && countResult && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1f1f23] border border-white/20 rounded-2xl max-w-md w-full p-6 space-y-5 shadow-2xl">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <AlertCircle className="text-amber-400" size={20} />
              Confirm Broadcast
            </h3>
            <p className="text-gray-300 text-sm">
              You are about to send this push message to{" "}
              <strong className="text-white font-bold">
                {countResult.targetCount} users
              </strong>
              .
            </p>

            <div className="bg-black/50 border border-white/10 rounded-lg p-3 text-xs text-gray-300 font-mono max-h-32 overflow-y-auto">
              {message}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 text-sm font-medium transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendAudiencePush}
                className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-bold transition"
              >
                Yes, Send Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
